import React from 'react';
import { BookOpen, Braces, CheckCircle, Clock, Database, ExternalLink, Gauge, Scale, ShieldCheck } from 'lucide-react';
import ProductPage, { ProductSection } from '../components/ProductPage';
import { useTranslations } from '../context/TranslationContext';
import usePageMetadata from '../hooks/usePageMetadata';
import { LEGISGRAPH_METRICS } from '../data/legisgraph';

const DATA = {
  it: {
    eyebrow: 'API · Knowledge graph giuridico',
    title: 'La conoscenza giuridica, strutturata per essere integrata.',
    description: 'LegisGraph connette leggi, sentenze e atti normativi italiani ed europei in un knowledge graph proprietario, interrogabile tramite API GraphQL.',
    contact: 'Contattaci', docs: 'Documentazione GraphQL', subject: 'Richiesta informazioni LegisGraph',
    whatTitle: 'Oltre la ricerca testuale',
    whatIntro: 'LegisGraph modella le relazioni tra i testi e permette di ricostruire il diritto vigente in qualsiasi data storica, la rete di citazioni e i collegamenti tra fonti ufficiali.',
    architectureTitle: 'Progettato per l’integrazione',
    architectureIntro: 'Un’infrastruttura API-first per legal-tech, compliance e applicazioni di intelligenza artificiale.',
    sourcesTitle: 'Fonti attive',
    sourcesIntro: 'Normativa, giurisprudenza e regolazione secondaria riunite in un unico grafo e aggiornate quotidianamente.',
    nextTitle: 'Prossime integrazioni',
    nextIntro: 'Il database è in continua espansione. Queste fonti sono in programma e non sono ancora incluse nella copertura attiva.',
    useCasesTitle: 'Casi d’uso',
    useCasesIntro: 'Dati legali connessi per ricerca, compliance e prodotti professionali.',
    serviceTitle: 'Obiettivi di servizio',
    serviceIntro: 'Target operativi indicativi, monitorati su base mensile e non intesi come vincoli contrattuali assoluti.',
  },
  en: {
    eyebrow: 'API · Legal knowledge graph',
    title: 'Legal knowledge, structured for integration.',
    description: 'LegisGraph connects Italian and European laws, judgments and regulatory acts in a proprietary knowledge graph accessible through GraphQL APIs.',
    contact: 'Contact us', docs: 'GraphQL documentation', subject: 'LegisGraph information request',
    whatTitle: 'Beyond text search',
    whatIntro: 'LegisGraph models relationships across legal texts, reconstructing the law in force at any historical date together with citations and links to official sources.',
    architectureTitle: 'Built for integration',
    architectureIntro: 'An API-first infrastructure for legal-tech, compliance and artificial intelligence applications.',
    sourcesTitle: 'Active sources',
    sourcesIntro: 'Legislation, case law and secondary regulation in one graph, updated daily.',
    nextTitle: 'Upcoming integrations',
    nextIntro: 'The database is continuously expanding. These sources are planned and are not yet part of the active coverage.',
    useCasesTitle: 'Use cases',
    useCasesIntro: 'Connected legal data for research, compliance, and professional products.',
    serviceTitle: 'Service objectives',
    serviceIntro: 'Indicative operational targets monitored monthly and not intended as absolute contractual commitments.',
  }
};

const activeSources = [
  ['Corte di Cassazione', 'Dal 2000 · oltre 1 M sentenze'],
  ['TAR — tutte le regioni', '2,5 M pronunce integrali e parziali'],
  ['Consiglio di Stato', '300.000 pronunce amministrative'],
  ['Corte di Giustizia UE', '70.000 pronunce integrali · InfoCuria'],
  ['Corte EDU', '70.000 pronunce integrali · ECHR'],
  ['Corte dei Conti', '100.000 pronunce integrali'],
  ['Corte Costituzionale', '20.000 pronunce integrali'],
  ['Giurisprudenza tributaria', '500.000 pronunce'],
  ['Leggi primarie italiane', '75.000 atti normativi'],
  ['Legislazione europea', '71.000 regolamenti e direttive UE'],
  ['Legislazione regionale', '47.000 atti · tutte le regioni'],
  ['EDPB · CCNL/CNEL', 'Regolazione europea e contrattazione collettiva'],
  ['Banca d’Italia · Consob · IVASS', 'Provvedimenti, circolari, delibere e regolamenti'],
];

const upcoming = ['UIF', 'EIOPA', 'AGCOM', 'Agenzia delle Dogane', 'Agenzia delle Entrate', 'Prassi MEF'];

const LegisGraphPage: React.FC = () => {
  const { currentLanguage, t } = useTranslations();
  const c = DATA[currentLanguage];
  const metricLabels = currentLanguage === 'it'
    ? { nodes: 'Nodi nel grafo', relationships: 'Relazioni indicizzate', judgments: 'Sentenze', acts: 'Atti normativi' }
    : { nodes: 'Graph nodes', relationships: 'Indexed relationships', judgments: 'Judgments', acts: 'Regulatory acts' };
  usePageMetadata(`LegisGraph | LegisRatio`, c.description);

  const capabilities = currentLanguage === 'it' ? [
    ['API GraphQL', 'Schema standard e accesso programmatico al grafo.', Braces],
    ['Dettaglio granulare', 'Relazioni fino ad articolo, comma e paragrafo.', Database],
    ['Cloud europeo', 'Architettura scalabile, alta disponibilità e data residency UE.', ShieldCheck],
    ['Contesto temporale', 'Testo vigente e relazioni giuridiche a una data specifica.', Clock],
  ] : [
    ['GraphQL API', 'Standard schema and programmatic graph access.', Braces],
    ['Granular detail', 'Relationships down to article, paragraph and subparagraph.', Database],
    ['European cloud', 'Scalable architecture, high availability and EU data residency.', ShieldCheck],
    ['Temporal context', 'Law in force and legal relationships at a specific date.', Clock],
  ];

  const sources = currentLanguage === 'it' ? activeSources : [
    ['Court of Cassation', 'Since 2000 · over 1M judgments'],
    ['Regional administrative courts', '2.5M full and partial decisions'],
    ['Council of State', '300,000 administrative decisions'],
    ['Court of Justice of the EU', '70,000 full decisions · InfoCuria'],
    ['European Court of Human Rights', '70,000 full decisions · ECHR'],
    ['Court of Auditors', '100,000 full decisions'],
    ['Constitutional Court', '20,000 full decisions'],
    ['Tax case law', '500,000 decisions'],
    ['Italian primary legislation', '75,000 regulatory acts'],
    ['European legislation', '71,000 EU regulations and directives'],
    ['Regional legislation', '47,000 acts · every region'],
    ['EDPB · collective agreements/CNEL', 'European regulation and collective bargaining'],
    ['Bank of Italy · Consob · IVASS', 'Measures, circulars, resolutions and regulations'],
  ];
  const serviceMetrics = currentLanguage === 'it'
    ? [['99.5%', 'Disponibilità mensile'], ['< 4.0 s', 'Latenza API p95'], ['< 7.0 s', t('legisgraph_search_latency')], ['4 h', t('legisgraph_recovery_time')]]
    : [['99.5%', 'Monthly availability'], ['< 4.0 s', 'API latency p95'], ['< 7.0 s', 'Search latency p95'], ['4 h', 'Recovery time objective']];

  const useCases = currentLanguage === 'it' ? [
    ['RAG per AI generativa', 'Fornisce contesto, relazioni e rimandi ufficiali per eliminare le allucinazioni.', BookOpen],
    ['Compliance e risk management', 'Mappa gli impatti a valle delle modifiche normative.', Scale],
    ['Software per professionisti', 'Integra ricerca e reti di citazioni nei prodotti esistenti.', ExternalLink],
  ] : [
    ['RAG for generative AI', 'Provides context, relationships and official references to eliminate hallucinations.', BookOpen],
    ['Compliance and risk management', 'Maps downstream impact from regulatory changes.', Scale],
    ['Professional software', 'Embeds research and citation networks in existing products.', ExternalLink],
  ];

  return (
    <ProductPage eyebrow={c.eyebrow} title={c.title} description={c.description}
      primaryLabel={c.docs} primaryHref="https://legisratio.gitbook.io/docs/graphql-api/graphql-api"
      contactLabel={c.contact} contactSubject={c.subject}>
      <ProductSection title={c.whatTitle} intro={c.whatIntro}>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {LEGISGRAPH_METRICS.map(({ value, key }) => <div key={key} className="glass-panel rounded-2xl p-6"><div className="text-3xl font-bold text-gradient-ai">{value}</div><div className="mt-2 text-sm text-gray-400">{metricLabels[key]}</div></div>)}
        </div>
      </ProductSection>
      <ProductSection title={c.architectureTitle} intro={c.architectureIntro}>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {capabilities.map(([title, text, Icon]) => <div key={title as string} className="glass-panel rounded-2xl p-6"><Icon className="mb-5 text-cosmic-teal" size={30}/><h3 className="font-bold">{title as string}</h3><p className="mt-3 text-base leading-relaxed text-gray-400">{text as string}</p></div>)}
        </div>
      </ProductSection>
      <ProductSection title={c.sourcesTitle} intro={c.sourcesIntro}>
        <div className="grid gap-3 md:grid-cols-2">
          {sources.map(([name, coverage]) => <div key={name} className="flex gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4"><CheckCircle className="mt-0.5 shrink-0 text-cosmic-teal" size={20}/><div><h3 className="font-semibold">{name}</h3><p className="mt-1 text-base leading-relaxed text-gray-400">{coverage}</p></div></div>)}
        </div>
        <div className="mt-12 rounded-2xl border border-dashed border-mystic-violet/50 bg-mystic-violet/5 p-6">
          <h3 className="text-xl font-bold">{c.nextTitle}</h3><p className="mt-2 max-w-3xl text-gray-400">{c.nextIntro}</p>
          <div className="mt-5 flex flex-wrap gap-3">{upcoming.map(source => <span key={source} className="rounded-full border border-white/10 px-4 py-2 text-sm">{source}</span>)}</div>
        </div>
      </ProductSection>
      <ProductSection title={c.useCasesTitle} intro={c.useCasesIntro}>
        <div className="grid gap-6 md:grid-cols-3">{useCases.map(([title, text, Icon]) => <div key={title as string} className="glass-panel rounded-2xl p-7"><Icon className="mb-5 text-nebula-pink" size={32}/><h3 className="text-xl font-bold">{title as string}</h3><p className="mt-3 leading-relaxed text-gray-400">{text as string}</p></div>)}</div>
      </ProductSection>
      <ProductSection title={c.serviceTitle} intro={c.serviceIntro}>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {serviceMetrics.map(([value,label]) => <div key={label} className="rounded-2xl border border-white/10 p-6"><Gauge className="mb-4 text-mystic-violet"/><div className="text-2xl font-bold">{value}</div><div className="mt-2 text-sm text-gray-400">{label}</div></div>)}
        </div>
      </ProductSection>
    </ProductPage>
  );
};

export default LegisGraphPage;
